import {
  Company_Read,
  IamRole_Read as UserRoleProto,
  NotificationMethod,
  StytchMemberStatus,
  TimeZone,
  User_Create,
  User_Create_In_Company,
  User_Read,
  User_Read_Minimal,
  User_Read_Nested,
  User_Read_Typeahead,
  User_Update,
  UserPreference_Appearance,
  UserPreference_Notification_Read,
  UserPreferences_Read,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import parsePhoneNumber from 'libphonenumber-js';

import {
  UserNotificationsPreferenceDTO,
  UserPreferenceNotificationKey,
} from '~components/UserForm/UserNotificationsPreferenceForm';
import { data as enums, SystemRoles } from '~constants/enums';
import { CompanyBasic } from '~hooks/useCompany/models';
import { BasicEquipment } from '~hooks/useEquipment/models';
import { BasicLabel } from '~hooks/useLabels/models';
import { Nullable } from '~types/Nullable';

export const getSelectedNotificationMethodsByNotification = (
  dto: Partial<UserNotificationsPreferenceDTO>,
) => {
  return Object.entries(dto).reduce(
    (obj, [notification, methods]) => {
      const availableMethods = Object.keys(methods) as NotificationMethod[];
      const selectedMethods = availableMethods.filter((method) => methods[method]);

      obj[notification as UserPreferenceNotificationKey] = selectedMethods;

      return obj;
    },
    {} as Record<UserPreferenceNotificationKey, NotificationMethod[]>,
  );
};

export class RoleItem {
  public static parse(proto: UserRoleProto) {
    return new RoleItem(
      proto?.id ?? '',
      proto.name ?? '',
      proto?.created_at ?? '',
      proto?.updated_at ?? '',
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }

  public get id() {
    return this._id;
  }

  public get name() {
    return this._name;
  }

  public get createdAt() {
    return this._createdAt;
  }

  public get updatedAt() {
    return this._updatedAt;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  constructor(
    private _id: string,
    private _name: string,
    private _createdAt: Nullable<string>,
    private _updatedAt: Nullable<string>,
    private _discarded_at: Nullable<Dayjs> = null,
  ) {}
}

export class UserPreferenceNotification {
  constructor(
    private _pending_job_reminder: Nullable<NotificationMethod[]>,
    private _new_job_request: Nullable<NotificationMethod[]>,
    private _start_job_reminder: Nullable<NotificationMethod[]>,
    private _order_pending_request: Nullable<NotificationMethod[]>,
    private _order_accepted: Nullable<NotificationMethod[]>,
    private _order_rejected: Nullable<NotificationMethod[]>,
    private _order_canceled: Nullable<NotificationMethod[]>,
    private _order_completed: Nullable<NotificationMethod[]>,
    private _no_location_alert: Nullable<NotificationMethod[]>,
    private _job_accepted: Nullable<NotificationMethod[]>,
    private _job_canceled: Nullable<NotificationMethod[]>,
    private _job_completed: Nullable<NotificationMethod[]>,
    private _job_rejected: Nullable<NotificationMethod[]>,
    private _load_completed: Nullable<NotificationMethod[]>,
    private _compliance_expiration_warning: Nullable<NotificationMethod[]>,
    private _order_created: Nullable<NotificationMethod[]>,
    private _project_created: Nullable<NotificationMethod[]>,
    private _new_message: Nullable<NotificationMethod[]>,
  ) {}

  public get pendingJobReminder() {
    return this._pending_job_reminder;
  }

  public get newJobRequest() {
    return this._new_job_request;
  }

  public get startJobReminder() {
    return this._start_job_reminder;
  }

  public get orderPendingRequest() {
    return this._order_pending_request;
  }

  public get orderAccepted() {
    return this._order_accepted;
  }

  public get orderRejected() {
    return this._order_rejected;
  }

  public get orderCanceled() {
    return this._order_canceled;
  }

  public get orderCompleted() {
    return this._order_completed;
  }

  public get noLocationAlert() {
    return this._no_location_alert;
  }

  public get jobAccepted() {
    return this._job_accepted;
  }

  public get jobCanceled() {
    return this._job_canceled;
  }

  public get jobCompleted() {
    return this._job_completed;
  }

  public get jobRejected() {
    return this._job_rejected;
  }

  public get loadCompleted() {
    return this._load_completed;
  }

  public get complianceExpirationWarning() {
    return this._compliance_expiration_warning;
  }

  public get orderCreated() {
    return this._order_created;
  }

  public get projectCreated() {
    return this._project_created;
  }

  public get newMessage() {
    return this._new_message;
  }

  public static parse(proto: UserPreference_Notification_Read) {
    return new UserPreferenceNotification(
      proto.pending_job_reminder ?? null,
      proto.new_job_request ?? null,
      proto.start_job_reminder ?? null,
      proto.order_pending_request ?? null,
      proto.order_accepted ?? null,
      proto.order_rejected ?? null,
      proto.order_canceled ?? null,
      proto.order_completed ?? null,
      proto.no_location_alert ?? null,
      proto.job_accepted ?? null,
      proto.job_canceled ?? null,
      proto.job_completed ?? null,
      proto.job_rejected ?? null,
      proto.load_completed ?? null,
      proto.compliance_expiration_warning ?? null,
      proto.order_created ?? null,
      proto.project_created ?? null,
      proto.new_message ?? null,
    );
  }
}

// AppearanceTheme is the string form of the spec's ThemePreference enum, so it
// stays in sync with the api-spec and matches the string-based ThemeModePreference
// in ~contexts/ThemeMode/themeModeStorage.
export type AppearanceTheme = `${UserPreference_Appearance['theme']}`;

class UserPreferenceAppearance {
  constructor(private _theme: Nullable<AppearanceTheme>) {}

  public get theme() {
    return this._theme;
  }

  public static parse(proto: UserPreference_Appearance) {
    return new UserPreferenceAppearance(proto.theme ?? null);
  }
}

export class UserPreferences {
  constructor(
    private _notifications: Nullable<UserPreferenceNotification>,
    private _appearance: Nullable<UserPreferenceAppearance> = null,
  ) {}

  public get notifications() {
    return this._notifications;
  }

  public get appearance() {
    return this._appearance;
  }

  public static parse(proto: UserPreferences_Read) {
    return new UserPreferences(
      proto.notifications ? UserPreferenceNotification.parse(proto.notifications) : null,
      proto.appearance ? UserPreferenceAppearance.parse(proto.appearance) : null,
    );
  }
}

// TODO: this should really extend UserBasic, see Driver/DriverBasic for example
export class User {
  public static parse(proto: User_Read, intercomHash: string | null = null) {
    return new User(
      proto.id ?? '',
      proto?.first_name ?? '',
      proto.last_name ?? '',
      proto?.company ? CompanyBasic.parse(proto?.company as Company_Read) : null,
      proto?.iam_roles?.length
        ? proto?.iam_roles.map((userSingleRole) => {
            return RoleItem.parse(userSingleRole as UserRoleProto);
          })
        : [],
      proto.email,
      proto.phone,
      proto?.stytch_member_id ?? null,
      proto?.stytch_member_status ?? undefined,
      proto?.stytch_user_id ?? null,
      proto?.stytch_user_status ?? undefined,
      proto?.created_at ?? '',
      proto?.updated_at ?? '',
      proto?.time_zone ?? enums.time_zone.default,
      intercomHash,
      proto?.preferences ? UserPreferences.parse(proto.preferences) : null,
      proto?.text_to_accept_enabled,
      proto?.external_id,
      proto?.equipment ? BasicEquipment.parse(proto.equipment) : null,
      proto?.additional_equipment?.map((e) => BasicEquipment.parse(e)) ?? [],
      proto?.last_login_at ? dayjs(proto.last_login_at) : null,
      proto?.labels?.map((label) => BasicLabel.parse(label)) ?? [],
      proto.compliant ?? null,
      proto?.accounting_id ?? null,
      proto?.hide_rates ?? false,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
      proto.intrastate_hours_of_service,
    );
  }

  private static deparseFields(proto: User) {
    const data: Partial<User_Update | User_Create> = {
      iam_role_ids: proto.userRoles?.map((role: RoleItem) => role.id),
      first_name: proto.firstName,
      last_name: proto.lastName,
      ...(proto.email ? { email: proto.email } : {}),
      ...(proto.phone ? { phone: proto.phone.replace(/\s/g, '') } : {}),
      time_zone: proto.timeZone as TimeZone,
      external_id: proto.externalId,
      accounting_id: proto.accountingId,
      ...(proto.equipment !== undefined
        ? { equipment_id: proto.equipment?.id ?? null }
        : {}),
      ...(proto.additionalEquipment !== undefined
        ? {
            additional_equipment_ids: Array.isArray(proto.additionalEquipment)
              ? proto.additionalEquipment.map((e) => e.id)
              : proto.additionalEquipment
                ? [(proto.additionalEquipment as unknown as BasicEquipment).id]
                : [],
          }
        : {}),
      label_ids: proto.labels?.map((label: BasicLabel) => label.id) ?? [],
    };

    // Only the v2 UserForm attaches hideRates as an own property, and only when the viewer
    // holds edit_user_hide_rate. Real User instances expose hideRates via a prototype getter,
    // so the own-property check makes every other caller (inline grid updates, profile saves)
    // omit the key entirely — it must never be sent by viewers who can't change it
    if (Object.hasOwn(proto, 'hideRates')) {
      data.hide_rates = Boolean(proto.hideRates);
    }

    // Same own-property rule: only the driver form attaches intrastateHoursOfService
    if (Object.hasOwn(proto, 'intrastateHoursOfService')) {
      data.intrastate_hours_of_service = Boolean(proto.intrastateHoursOfService);
    }

    return data;
  }

  public static deparse(
    proto: User,
    invitesToSend?: User_Create['send_invite'],
    userNotificationsPreferenceData?: UserNotificationsPreferenceDTO,
  ): User_Update | User_Create {
    const data = User.deparseFields(proto);

    if (proto.id) {
      const updateUserData = data as User_Update;

      if (userNotificationsPreferenceData) {
        updateUserData.preferences = {
          notifications: getSelectedNotificationMethodsByNotification(
            userNotificationsPreferenceData,
          ),
        };
      }

      return updateUserData;
    }

    const createUserData = data as User_Create;
    createUserData.company_id = String(proto.company?.id);
    createUserData.send_invite = invitesToSend;

    return createUserData;
  }

  // The company comes from the company-scoped create endpoint's path; that schema rejects a body company_id
  public static deparseForCompanyCreate(
    proto: User,
    invitesToSend?: User_Create_In_Company['send_invite'],
  ) {
    const createUserData = User.deparseFields(proto) as User_Create_In_Company;
    createUserData.send_invite = invitesToSend;

    return createUserData;
  }

  public get id() {
    return this._id;
  }

  public get firstName() {
    return this._firstName;
  }

  public get lastName() {
    return this._lastName;
  }
  public get fullName() {
    return `${this._firstName} ${this._lastName}`;
  }
  public get company() {
    return this._company;
  }

  public get userRoles() {
    return this._userRoles;
  }

  // The API names the role "Salesperson"; SystemRoles.SALESPERSON is lowercase.
  public get isSalesperson() {
    return Boolean(
      this._userRoles?.some(
        (role) => role.name.toLowerCase() === SystemRoles.SALESPERSON.toLowerCase(),
      ),
    );
  }

  public get email() {
    return this._email;
  }

  public get phone() {
    return this._phone;
  }

  public get createdAt() {
    return this._createdAt;
  }

  public get updatedAt() {
    return this._updatedAt;
  }

  public get status() {
    return this._stytch_member_status;
  }

  /**
   * An active member is someone that is an active Stytch Member, meaning they have been connected by email
   */
  public get isActiveMember() {
    return this._stytch_member_status === StytchMemberStatus.ACTIVE;
  }

  /**
   * An active user is someone that is an active Stytch User, meaning they have been connected by phone
   */
  public get isActiveUser() {
    return this._stytch_user_status === StytchMemberStatus.ACTIVE;
  }

  public get timeZone() {
    return this._time_zone;
  }

  public get intercomHash() {
    return this._intercom_hash ?? '';
  }

  public get preferences() {
    return this._preferences;
  }

  public get textToAcceptEnabled() {
    return this._text_to_accept_enabled;
  }

  public get externalId() {
    return this._external_id;
  }

  public get accountingId() {
    return this._accounting_id;
  }

  public get equipment() {
    return this._equipment;
  }

  public set equipment(equipment: Nullable<BasicEquipment>) {
    this._equipment = equipment;
  }

  public get additionalEquipment() {
    return this._additional_equipment;
  }

  public set additionalEquipment(equipment: BasicEquipment[]) {
    this._additional_equipment = equipment;
  }

  public get lastLoginAt() {
    return this._last_login_at;
  }

  public get labels() {
    return this._labels;
  }

  public get compliant() {
    return this._compliant;
  }

  public get hideRates() {
    return this._hide_rates;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public get intrastateHoursOfService() {
    return this._intrastate_hours_of_service;
  }

  constructor(
    private _id: string,
    private _firstName: string,
    private _lastName: string,
    private _company: Nullable<CompanyBasic>,
    private _userRoles: Nullable<RoleItem[]>,
    private _email: Nullable<string>,
    private _phone: Nullable<string>,
    private _stytch_member_id: string | null,
    private _stytch_member_status: StytchMemberStatus,
    private _stytch_user_id: string | null,
    private _stytch_user_status: StytchMemberStatus,
    private _createdAt: string,
    private _updatedAt: string,
    private _time_zone: string,
    private _intercom_hash: string | null,
    private _preferences: Nullable<UserPreferences>,
    private _text_to_accept_enabled: boolean,
    private _external_id: string | null,
    private _equipment: Nullable<BasicEquipment>,
    private _additional_equipment: BasicEquipment[],
    private _last_login_at: Dayjs | null,
    private _labels: BasicLabel[],
    private _compliant: boolean | null = null,
    private _accounting_id: string | null = null,
    private _hide_rates: boolean = false,
    private _discarded_at: Dayjs | null = null,
    private _intrastate_hours_of_service: boolean = false,
  ) {}
}

export class UserBasic {
  constructor(
    private _id: Nullable<string>,
    private _first_name: string,
    private _last_name: string,
    private _email: Nullable<string>,
    private _phone: Nullable<string>,
    private _discarded_at: Dayjs | null = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get firstName() {
    return this._first_name;
  }

  public get lastName() {
    return this._last_name;
  }

  public get email() {
    return this._email;
  }

  public get phone() {
    if (this._phone) {
      const phone = this._phone.replace(/^\+/g, '');
      return parsePhoneNumber(`+${phone}`)?.formatInternational() as string;
    }
  }

  public get fullName() {
    return `${this.firstName} ${this.lastName}`;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public static parse(proto: User_Read_Nested) {
    return new UserBasic(
      proto.id,
      proto.first_name,
      proto.last_name,
      proto.email,
      proto.phone,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
}

export class UserMinimal {
  constructor(
    private _id: Nullable<string>,
    private _first_name: string,
    private _last_name: string,
    private _discarded_at: Nullable<Dayjs>,
    private _company: Nullable<CompanyBasic>,
  ) {}

  public get id() {
    return this._id;
  }

  public get firstName() {
    return this._first_name;
  }

  public get lastName() {
    return this._last_name;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public get company() {
    return this._company;
  }

  public get fullName() {
    return `${this._first_name} ${this._last_name}`;
  }

  public static parse(proto: User_Read_Minimal) {
    return new UserMinimal(
      proto.id,
      proto.first_name,
      proto.last_name,
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
      proto.company ? CompanyBasic.parse(proto.company) : null,
    );
  }
}

export class UserTypeahead {
  constructor(
    private _id: string,
    private _first_name: string,
    private _last_name: string,
  ) {}

  public get id() {
    return this._id;
  }

  public get firstName() {
    return this._first_name;
  }

  public get lastName() {
    return this._last_name;
  }

  public get fullName() {
    return `${this._first_name} ${this._last_name}`;
  }

  public static parse(proto: User_Read_Typeahead) {
    return new UserTypeahead(proto.id, proto.first_name, proto.last_name);
  }
}
